import { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';

function SearchIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15Z" stroke="currentColor" strokeWidth="2" />
      <path d="M16 16l4.5 4.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function IconEdit() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 20h9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path
        d="M16.5 3.5a2.12 2.12 0 0 1 3 3L8 18l-4 1 1-4L16.5 3.5Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconTrash() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M3 6h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M8 6V4h8v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path
        d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

export default function ClauseLibrary() {
  const { user } = useAuth();
  const [tab, setTab] = useState('msa');
  const [clauses, setClauses] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ sort_order: '', code: '', clause_title: '', template_name: '', template_text: '' });
  const [search, setSearch] = useState('');
  const [mandatoryOnly, setMandatoryOnly] = useState(false);
  const [formAccordionOpen, setFormAccordionOpen] = useState(false);
  const formRef = useRef(null);

  const load = () => api.get(`/clauses/${tab}`).then(setClauses);
  useEffect(() => { load(); }, [tab]);

  useEffect(() => {
    if (editing) setFormAccordionOpen(true);
  }, [editing]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const highlightMatch = (text) => {
    const raw = text == null ? '' : String(text);
    if (!search.trim()) return raw;
    const q = search.toLowerCase();
    const lower = raw.toLowerCase();
    const idx = lower.indexOf(q);
    if (idx < 0) return raw;
    return (
      <>
        {raw.slice(0, idx)}
        <mark className="clause-lib-mark">{raw.slice(idx, idx + search.length)}</mark>
        {raw.slice(idx + search.length)}
      </>
    );
  };

  const handleSave = async () => {
    try {
      if (editing) await api.put(`/clauses/${tab}/${editing}`, form);
      else await api.post(`/clauses/${tab}`, form);
      setEditing(null);
      setForm({ sort_order: '', code: '', clause_title: '', template_name: '', template_text: '' });
      load();
    } catch (e) { alert(e.message); }
  };

  const handleEdit = useCallback((c) => {
    setEditing(c.id);
    setForm({ sort_order: c.sort_order, code: c.code, clause_title: c.clause_title, template_name: c.template_name, template_text: c.template_text });
    requestAnimationFrame(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this clause template?')) return;
    await api.del(`/clauses/${tab}/${id}`);
    load();
  };

  const toggleMandatory = async (clauseTitle, currentVal) => {
    await api.put(`/clauses/${tab}/mandatory`, { clause_title: clauseTitle, is_mandatory: currentVal ? 0 : 1 });
    load();
  };

  const q = search.toLowerCase();
  const matches = clauses.filter(c => {
    if (mandatoryOnly && !c.is_mandatory) return false;
    if (!q) return true;
    return [c.clause_title, c.code, c.template_name, c.template_text].some(v => (v || '').toLowerCase().includes(q));
  });
  const grouped = {};
  matches.forEach(c => {
    if (!grouped[c.clause_title]) grouped[c.clause_title] = [];
    grouped[c.clause_title].push(c);
  });

  const sortedGroups = Object.entries(grouped).sort((a, b) => {
    const ao = Number(a[1][0]?.sort_order) || 0;
    const bo = Number(b[1][0]?.sort_order) || 0;
    return ao - bo;
  });

  const isAdmin = user?.role === 'Admin';
  const tabLabel = tab === 'msa' ? 'MSA' : 'SOW';
  const templateCount = matches.length;

  return (
    <>
      <div className="page-header">
        <h1>Clause Library</h1>
      </div>
      <div className="page-content clause-library-page">
        <div className="clause-lib-tab-strip" role="tablist" aria-label="Clause library type">
          <button
            type="button"
            role="tab"
            id="clause-tab-msa"
            aria-selected={tab === 'msa'}
            aria-controls="clause-lib-panel"
            className={`clause-lib-tab ${tab === 'msa' ? 'clause-lib-tab-active' : ''}`}
            onClick={() => setTab('msa')}
          >
            MSA Clauses
          </button>
          <button
            type="button"
            role="tab"
            id="clause-tab-sow"
            aria-selected={tab === 'sow'}
            aria-controls="clause-lib-panel"
            className={`clause-lib-tab ${tab === 'sow' ? 'clause-lib-tab-active' : ''}`}
            onClick={() => setTab('sow')}
          >
            SOW Clauses
          </button>
        </div>

        <div
          id="clause-lib-panel"
          role="tabpanel"
          aria-labelledby={tab === 'msa' ? 'clause-tab-msa' : 'clause-tab-sow'}
        >
          <div className="clause-lib-stats" aria-label="Filtered clause counts">
            <div className="clause-lib-stat-card" title="Distinct clause groups matching filters">
              <div className="clause-lib-stat-card-label">Groups</div>
              <div className="clause-lib-stat-card-value">{sortedGroups.length.toLocaleString()}</div>
            </div>
            <div className="clause-lib-stat-card" title="Clause templates matching filters">
              <div className="clause-lib-stat-card-label">Templates</div>
              <div className="clause-lib-stat-card-value">{templateCount.toLocaleString()}</div>
            </div>
          </div>

          <div className="clause-lib-toolbar">
            <div className="clause-lib-search-wrap">
              <SearchIcon />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search title, code, template name, or body…"
                aria-label="Search clauses"
              />
            </div>
            <div className="clause-lib-toolbar-meta">
              <label className="clause-lib-filter">
                <input
                  type="checkbox"
                  checked={mandatoryOnly}
                  onChange={e => setMandatoryOnly(e.target.checked)}
                />
                Mandatory only
              </label>
            </div>
          </div>

          {isAdmin && (
            <div
              className={`card clause-lib-form-card clause-lib-accordion${formAccordionOpen ? ' clause-lib-accordion--open' : ''}`}
              ref={formRef}
            >
              <button
                type="button"
                className="clause-lib-accordion-toggle"
                id="clause-lib-form-accordion-trigger"
                aria-expanded={formAccordionOpen}
                aria-controls="clause-lib-form-accordion-panel"
                onClick={() => setFormAccordionOpen((o) => !o)}
              >
                <span className="clause-lib-accordion-chevron" aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </span>
                <span className="clause-lib-accordion-toggle-text">
                  <span className="clause-lib-accordion-title">{editing ? 'Edit template' : 'Add new template'}</span>
                  <span className="clause-lib-form-hint">
                    {tabLabel} library · {editing ? 'Update the row below, then save.' : 'Creates a new row under the clause title you enter.'}
                  </span>
                </span>
              </button>
              <div
                id="clause-lib-form-accordion-panel"
                className="clause-lib-accordion-panel"
                role="region"
                aria-labelledby="clause-lib-form-accordion-trigger"
                aria-hidden={!formAccordionOpen}
                hidden={!formAccordionOpen}
              >
                <div className="clause-lib-accordion-panel-inner">
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Sort order</label>
                      <input type="number" value={form.sort_order} onChange={e => set('sort_order', e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label>Code</label>
                      <input value={form.code} onChange={e => set('code', e.target.value)} placeholder="e.g. CL-SCOPE" />
                    </div>
                    <div className="form-group">
                      <label>Clause title</label>
                      <input value={form.clause_title} onChange={e => set('clause_title', e.target.value)} placeholder="e.g. Scope of Services" />
                    </div>
                    <div className="form-group">
                      <label>Template name</label>
                      <input value={form.template_name} onChange={e => set('template_name', e.target.value)} placeholder="e.g. Standard SOW-based" />
                    </div>
                    <div className="form-group full-width">
                      <label>Template text</label>
                      <textarea
                        rows={5}
                        value={form.template_text}
                        onChange={e => set('template_text', e.target.value)}
                        placeholder="Use {{variable_name}} for dynamic fields"
                      />
                    </div>
                  </div>
                  <div className="btn-group mt-4">
                    <button type="button" className="btn btn-primary" onClick={handleSave}>
                      {editing ? 'Update template' : 'Add template'}
                    </button>
                    {editing && (
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => {
                          setEditing(null);
                          setForm({ sort_order: '', code: '', clause_title: '', template_name: '', template_text: '' });
                        }}
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {sortedGroups.map(([title, templates]) => {
            const isMandatory = templates[0].is_mandatory;
            const sortOrder = templates[0].sort_order;
            return (
              <div className="card clause-lib-group" key={title}>
                <div className="card-header">
                  <div className="clause-lib-group-title-row">
                    <span className="clause-lib-order" title="Sort order">{sortOrder}</span>
                    <span className="clause-lib-group-title">{highlightMatch(title)}</span>
                    {isMandatory && !isAdmin && (
                      <span className="badge badge-approved">Mandatory</span>
                    )}
                    <span className="clause-lib-code-pill">{highlightMatch(templates[0].code)}</span>
                  </div>
                  <div className="clause-lib-group-actions">
                    {isAdmin && (
                      <label className="clause-lib-mandatory-label">
                        <input
                          type="checkbox"
                          checked={!!isMandatory}
                          onChange={() => toggleMandatory(title, isMandatory)}
                          aria-label={`Mandatory for clause group ${title}`}
                        />
                        Mandatory
                      </label>
                    )}
                  </div>
                </div>
                <div className="table-container">
                  <table>
                    <thead>
                      <tr>
                        <th>Template name</th>
                        <th>Template text</th>
                        {isAdmin && <th className="text-right clause-lib-actions-th">Actions</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {templates.map(t => (
                        <tr key={t.id}>
                          <td className="clause-lib-template-name">{highlightMatch(t.template_name)}</td>
                          <td>
                            <div className="clause-lib-template-text">{highlightMatch(t.template_text)}</div>
                          </td>
                          {isAdmin && (
                            <td className="clause-lib-actions-cell text-right">
                              <div className="clause-lib-row-actions">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline clause-lib-icon-btn"
                                  onClick={() => handleEdit(t)}
                                  title="Edit template"
                                  aria-label={`Edit template ${t.template_name || t.code || ''}`}
                                >
                                  <IconEdit />
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-danger clause-lib-icon-btn"
                                  onClick={() => handleDelete(t.id)}
                                  title="Delete template"
                                  aria-label={`Delete template ${t.template_name || t.code || ''}`}
                                >
                                  <IconTrash />
                                </button>
                              </div>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}

          {sortedGroups.length === 0 && (
            <div className="empty-state clause-lib-empty">
              <h3>No clauses match your filters</h3>
              <p>
                {mandatoryOnly || search.trim()
                  ? 'Try clearing search or the mandatory filter.'
                  : `No ${tabLabel} clause templates are loaded yet.`}
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
